import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatMonth, getStatusLabel, getPaymentTypeLabel } from './utils';

// Read every page with the same role-scoped endpoint and captured filters.
// Never save a partial report when data changes or a later request fails.
export async function readPaymentsForExport(apiUrl, range, status) {
  const rows = [];
  const ids = new Set();
  let total = null;
  do {
    const params = { limit: 200, skip: rows.length };
    if (range.startMonth) params.start_month = range.startMonth;
    if (range.endMonth) params.end_month = range.endMonth;
    if (status) params.status = status;
    const { data } = await axios.get(`${apiUrl}/api/payments`, { params, timeout: 30000 });
    if (!Array.isArray(data.data) || !Number.isInteger(data.total) || data.total < 0) throw new Error('Data laporan belum lengkap. Silakan coba lagi.');
    if (total !== null && total !== data.total) throw new Error('Data pembayaran berubah. Silakan ulangi export.');
    total = data.total;
    for (const row of data.data) {
      if (!row.id || ids.has(row.id) || !Number.isFinite(row.amount) || row.amount < 0) throw new Error('Data laporan tidak konsisten. Silakan muat ulang.');
      ids.add(row.id); rows.push(row);
    }
    if (rows.length > total || (!data.data.length && rows.length < total)) throw new Error('Data laporan belum lengkap. Silakan coba lagi.');
  } while (rows.length < total);
  return rows;
}

const green = [26, 77, 55];
const muted = [100, 112, 106];
const money = amount => 'Rp ' + new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 }).format(amount);
const clean = value => String(value ?? '').replace(/[\u0000-\u001f\u007f]/g, ' ').trim() || '-';
export const paymentPeriodLabel = range => {
  if (range.startMonth && range.endMonth) return range.startMonth === range.endMonth ? formatMonth(range.startMonth) : `${formatMonth(range.startMonth)} - ${formatMonth(range.endMonth)}`;
  if (range.startMonth) return `Mulai ${formatMonth(range.startMonth)}`;
  if (range.endMonth) return `Sampai ${formatMonth(range.endMonth)}`;
  return 'Semua periode';
};
const paidDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta', day: '2-digit', month: 'short', year: 'numeric' });
};
async function loadLogo() {
  const response = await fetch('/logo-original.png', { cache: 'force-cache', signal: AbortSignal.timeout(15000) });
  if (!response.ok) throw new Error('Logo belum dapat dimuat. Silakan coba lagi.');
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Logo belum dapat dimuat.'));
    reader.readAsDataURL(blob);
  });
}

export async function createPaymentPdf(rows, { range, status, resident = false }) {
  const logo = await loadLogo();
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const now = new Date();
  const stamp = now.toLocaleString('id-ID', { timeZone: 'Asia/Jakarta', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) + ' WIB';
  const period = paymentPeriodLabel(range);
  const statusLabel = status ? getStatusLabel(status) : 'Semua status';
  const total = rows.reduce((sum, r) => sum + r.amount, 0);
  const paid = rows.filter(r => r.status === 'lunas');
  const pending = rows.filter(r => r.status !== 'lunas');
  const paidTotal = paid.reduce((sum, r) => sum + r.amount, 0);
  const logoInfo = pdf.getImageProperties(logo);
  const logoHeight = 20, logoWidth = logoHeight * logoInfo.width / logoInfo.height;
  pdf.setProperties({ title: `Laporan Pembayaran Kos Cemara - ${period}`, author: 'Tim Kos Cemara', subject: 'Rekap tagihan dan status pembayaran' });
  pdf.setFillColor(...green); pdf.rect(0, 0, width, 34, 'F');
  pdf.setFillColor(255, 255, 255); pdf.roundedRect(margin, 6, logoWidth + 6, 26, 2, 2, 'F');
  pdf.addImage(logo, 'PNG', margin + 3, 9, logoWidth, logoHeight);
  pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(18);
  pdf.text('KOS CEMARA', margin + logoWidth + 12, 16);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
  pdf.text('LAPORAN PEMBAYARAN', margin + logoWidth + 12, 24);
  pdf.setFontSize(9); pdf.text('koscemara.com', width - margin, 14, { align: 'right' });
  pdf.text(resident ? 'Ringkasan akun penghuni' : 'Rekap pengelolaan kos', width - margin, 21, { align: 'right' });
  pdf.setFillColor(191, 160, 93); pdf.rect(0, 34, width, 1, 'F');
  pdf.setTextColor(...green); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  pdf.text(`Periode tagihan: ${period}`, margin, 44);
  pdf.setTextColor(...muted); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  pdf.text(`Filter status: ${statusLabel}`, margin, 51);
  pdf.text(`Dibuat: ${stamp}`, width - margin, 44, { align: 'right' });
  pdf.text('Seluruh hasil filter, termasuk semua halaman', width - margin, 51, { align: 'right' });
  const cards = [
    ['JUMLAH TAGIHAN', `${rows.length} tagihan`, `${paid.length} lunas | ${pending.length} belum lunas`],
    ['TOTAL NILAI TAGIHAN', money(total), 'Sesuai periode dan status terpilih'],
    ['SUDAH DIBAYAR', money(paidTotal), 'Hanya tagihan berstatus lunas'],
    ['BELUM LUNAS', money(total - paidTotal), 'Termasuk menunggu verifikasi'],
  ];
  const gap = 4, cardWidth = (width - margin * 2 - gap * 3) / 4;
  cards.forEach(([label, value, caption], index) => {
    const x = margin + index * (cardWidth + gap);
    pdf.setFillColor(242, 246, 242); pdf.roundedRect(x, 57, cardWidth, 24, 2, 2, 'F');
    pdf.setTextColor(...muted); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7); pdf.text(label, x + 4, 63);
    pdf.setTextColor(...green); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(value.length > 20 ? 11 : 14); pdf.text(value, x + 4, 71);
    pdf.setTextColor(...muted); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7); pdf.text(caption, x + 4, 77);
  });
  pdf.setFontSize(8); pdf.text('Rekap berdasarkan bulan tagihan, bukan periode arus kas. Dokumen ini bukan kuitansi atau bukti pelunasan.', margin, 88);
  autoTable(pdf, {
    startY: 94,
    margin: { left: margin, right: margin, top: 25, bottom: 19 },
    head: [['No.', 'Penghuni / Kamar', 'Periode', 'Jenis', 'Jumlah', 'Status', 'Tgl. Bayar', 'Keterangan']],
    body: rows.map((r, i) => [String(i + 1), `${clean(r.tenant_name)}\n${clean(r.room_name)}`, formatMonth(r.month), getPaymentTypeLabel(r.payment_type), money(r.amount), getStatusLabel(r.status), paidDate(r.payment_date), clean(r.description)]),
    theme: 'plain', showHead: 'everyPage', rowPageBreak: 'avoid',
    styles: { font: 'helvetica', fontSize: 8, cellPadding: 3, textColor: [41, 53, 46], overflow: 'linebreak', valign: 'top', lineColor: [224, 231, 224], lineWidth: { bottom: 0.15 } },
    headStyles: { fillColor: green, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: [246, 248, 245] },
    columnStyles: { 0: { cellWidth: 12 }, 1: { cellWidth: 42 }, 2: { cellWidth: 28 }, 3: { cellWidth: 27 }, 4: { cellWidth: 32, halign: 'right' }, 5: { cellWidth: 31 }, 6: { cellWidth: 25 }, 7: { cellWidth: 'auto' } },
  });
  if (!rows.length) {
    pdf.setFontSize(11); pdf.setTextColor(...muted);
    pdf.text('Tidak ada tagihan yang sesuai dengan filter ini.', margin, 116);
  }
  const pages = pdf.internal.getNumberOfPages();
  for (let page = 1; page <= pages; page++) {
    pdf.setPage(page);
    if (page > 1) {
      pdf.setTextColor(...green); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
      pdf.text('KOS CEMARA  /  LAPORAN PEMBAYARAN', margin, 12);
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(...muted);
      pdf.text(`${period} | ${statusLabel}`, margin, 18);
    }
    pdf.setDrawColor(210, 220, 212); pdf.line(margin, height - 14, width - margin, height - 14);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7); pdf.setTextColor(...muted);
    pdf.text(`Tim Kos Cemara | ${stamp}`, margin, height - 9);
    pdf.text(`Halaman ${page} dari ${pages}`, width - margin, height - 9, { align: 'right' });
  }
  const date = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' });
  pdf.save(`Kos-Cemara-Pembayaran-${range.startMonth || 'semua'}-${range.endMonth || 'periode'}-${status || 'semua-status'}-${date}.pdf`);
}
